import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CubeProvider, useCubeQuery } from "@cubejs-client/react";
import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import cubeApi from "@/lib/cube";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CalendarIcon, Loader2, ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import type { DateRange } from "react-day-picker";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import GridLayout, { WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const ResponsiveGridLayout = WidthProvider(GridLayout);
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

// Helper functions (simplified versions from DashboardBuilder)
const formatFriendlyName = (value: string | number): string => {
  if (typeof value !== 'string') return String(value);
  return value
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const formatNumericValue = (value: any, measureName?: string): string => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (typeof numValue !== 'number' || isNaN(numValue)) return String(value);

  const measureLower = measureName?.toLowerCase() || '';
  const isCurrency = measureName && (
    measureLower.includes('amount') ||
    measureLower.includes('revenue') ||
    measureLower.includes('price') ||
    measureLower.includes('cost') ||
    measureLower.includes('value')
  );

  if (isCurrency) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numValue);
  }

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(numValue);
};

// Widget renderer component
const WidgetDisplay = ({ widget, dateRange }: any) => {
  const queryConfig: any = {
    measures: widget.measures,
    dimensions: widget.dimensions,
  };

  if (widget.filters && widget.filters.length > 0) {
    queryConfig.filters = widget.filters;
  }

  if (widget.timeDimension) {
    queryConfig.timeDimensions = [{
      dimension: widget.timeDimension.dimension,
      granularity: widget.timeDimension.granularity,
      dateRange: dateRange,
    }];
  }

  const { resultSet, isLoading } = useCubeQuery(
    widget.measures.length > 0 ? queryConfig : null
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!resultSet || widget.measures.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        No data available
      </div>
    );
  }

  const data = resultSet.tablePivot();

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        No data to display
      </div>
    );
  }

  const xAxisKey = widget.timeDimension
    ? `${widget.timeDimension.dimension}.${widget.timeDimension.granularity}`
    : widget.dimensions[0] || Object.keys(data[0])[0];

  const allValues = data.flatMap(item =>
    widget.measures.map(measure => Number(item[measure]) || 0)
  );
  const maxDataValue = Math.max(...allValues);
  const calculatedYMax = Math.ceil(maxDataValue * 1.5);

  // Render based on chart type
  switch (widget.chartType) {
    case "pie":
      const hasNonTimeDimensions = widget.dimensions.length > 0;
      let pieData: { name: string; value: number }[] = [];

      if (hasNonTimeDimensions) {
        const dimensionKey = widget.dimensions[0];
        const measure = widget.measures[0];
        const aggregated = new Map<string, number>();

        data.forEach(item => {
          const dimValue = item[dimensionKey];
          const measureValue = Number(item[measure]) || 0;
          if (dimValue) {
            const current = aggregated.get(dimValue) || 0;
            aggregated.set(dimValue, current + measureValue);
          }
        });

        pieData = Array.from(aggregated.entries()).map(([name, value]) => ({
          name: formatFriendlyName(name),
          value
        }));
      } else {
        pieData = data.map((item) => ({
          name: formatFriendlyName(item[xAxisKey]),
          value: item[widget.measures[0]],
        }));
      }

      return (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              outerRadius="80%"
              fill="#8884d8"
              dataKey="value"
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value: any) => formatNumericValue(value, widget.measures[0])} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );

    case "bar":
    case "line":
    case "area":
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ bottom: 60, left: 10, right: 10, top: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey={xAxisKey}
              tick={{ fontSize: 11 }}
              angle={-45}
              textAnchor="end"
              height={80}
              tickFormatter={(value) =>
                widget.timeDimension
                  ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : formatFriendlyName(value)
              }
            />
            <YAxis tick={{ fontSize: 11 }} domain={[0, calculatedYMax]} />
            <Tooltip
              formatter={(value: any, name: any) => [formatNumericValue(value, name), formatFriendlyName(name)]}
              labelFormatter={(value) => {
                if (widget.timeDimension && value) {
                  return new Date(value).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  });
                }
                return formatFriendlyName(value);
              }}
            />
            <Legend />
            {widget.measures.map((measure, index) => (
              <Bar
                key={measure}
                dataKey={measure}
                fill={COLORS[index % COLORS.length]}
                radius={[4, 4, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      );

    case "table":
      return (
        <div className="overflow-auto h-full">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                {Object.keys(data[0]).map((key) => (
                  <th key={key} className="px-4 py-2 text-left font-medium">
                    {formatFriendlyName(key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, index) => (
                <tr key={index} className="border-b">
                  {Object.entries(row).map(([key, value], i) => (
                    <td key={i} className="px-4 py-2">
                      {typeof value === "number"
                        ? formatNumericValue(value, key)
                        : String(value)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    default:
      return null;
  }
};

const AnalyticsDashboard = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [dashboard, setDashboard] = useState<any>(null);
  const [widgets, setWidgets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Initialize date range from saved dashboard settings or default to last 30 days
  const [dateFrom, setDateFrom] = useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });
  const [dateTo, setDateTo] = useState<Date>(new Date());
  const [selectedPreset, setSelectedPreset] = useState<string>("last_30_days");

  const [tempDateFrom, setTempDateFrom] = useState<Date | undefined>(dateFrom);
  const [tempDateTo, setTempDateTo] = useState<Date | undefined>(dateTo);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // Load dashboard and widgets
  useEffect(() => {
    const loadDashboard = async () => {
      if (!id) return;

      try {
        setLoading(true);

        // Load dashboard
        const { data: dashboardData, error: dashboardError } = await supabase
          .from('analytics_dashboards')
          .select('*')
          .eq('id', id)
          .single();

        if (dashboardError) throw dashboardError;

        setDashboard(dashboardData);

        // Load date range settings if they exist
        if (dashboardData.date_range_settings) {
          const settings = typeof dashboardData.date_range_settings === 'string'
            ? JSON.parse(dashboardData.date_range_settings)
            : dashboardData.date_range_settings;

          if (settings.startDate) {
            const start = new Date(settings.startDate);
            setDateFrom(start);
            setTempDateFrom(start);
          }
          if (settings.endDate) {
            const end = new Date(settings.endDate);
            setDateTo(end);
            setTempDateTo(end);
          }
          if (settings.preset) {
            setSelectedPreset(settings.preset);
          }
        }

        // Load widgets
        const { data: widgetsData, error: widgetsError } = await supabase
          .from('dashboard_widgets')
          .select('*')
          .eq('dashboard_id', id)
          .order('position');

        if (widgetsError) throw widgetsError;

        const transformedWidgets = (widgetsData || []).map((w) => {
          const sizeData = typeof w.size === 'string' ? JSON.parse(w.size) : (w.size || {});

          return {
            id: w.id,
            i: `widget-${w.id}`,
            title: w.title || '',
            chartType: w.chart_type,
            measures: Array.isArray(w.measures) ? w.measures : [],
            dimensions: Array.isArray(w.dimensions) ? w.dimensions : [],
            filters: Array.isArray(w.filters) ? w.filters : undefined,
            timeDimension: w.time_dimension,
            x: sizeData.x || 0,
            y: sizeData.y || 0,
            w: sizeData.w || 12,
            h: sizeData.h || 4,
          };
        });

        setWidgets(transformedWidgets);
      } catch (err) {
        console.error("Error loading dashboard:", err);
        toast({
          title: "Error",
          description: "Failed to load dashboard",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [id, toast]);

  const selectDatePreset = (preset: string) => {
    const today = new Date();
    let startDate: Date;
    let endDate: Date = new Date();

    switch (preset) {
      case "last_30_days":
        startDate = new Date(new Date().setDate(today.getDate() - 30));
        break;
      case "last_60_days":
        startDate = new Date(new Date().setDate(today.getDate() - 60));
        break;
      case "last_90_days":
        startDate = new Date(new Date().setDate(today.getDate() - 90));
        break;
      case "last_180_days":
        startDate = new Date(new Date().setDate(today.getDate() - 180));
        break;
      case "this_year":
        startDate = new Date(today.getFullYear(), 0, 1);
        break;
      case "last_year":
        startDate = new Date(today.getFullYear() - 1, 0, 1);
        endDate = new Date(today.getFullYear() - 1, 11, 31);
        break;
      case "custom":
        setSelectedPreset("custom");
        return;
      default:
        startDate = new Date(new Date().setDate(today.getDate() - 30));
    }

    setTempDateFrom(startDate);
    setTempDateTo(endDate);
    setSelectedPreset(preset);
  };

  const handleApplyDates = () => {
    if (tempDateFrom && tempDateTo) {
      setDateFrom(tempDateFrom);
      setDateTo(tempDateTo);
      setIsPopoverOpen(false);
    }
  };

  const formatDateForCube = (date: Date): string => {
    return format(date, "yyyy-MM-dd");
  };

  const dateRange = [formatDateForCube(dateFrom), formatDateForCube(dateTo)];

  if (loading) {
    return (
      <BackofficeLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="ml-4 text-muted-foreground">Loading dashboard...</p>
        </div>
      </BackofficeLayout>
    );
  }

  return (
    <BackofficeLayout>
      <CubeProvider cubeApi={cubeApi}>
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate("/backoffice/analytics")}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                {dashboard?.name || "Dashboard"}
              </h1>
              {dashboard?.description && (
                <p className="text-muted-foreground">{dashboard.description}</p>
              )}
            </div>
          </div>

          {/* Date Range Picker */}
          <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="min-w-[280px] justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateFrom && dateTo ? (
                  <>
                    {format(dateFrom, "MMM d, yyyy")} - {format(dateTo, "MMM d, yyyy")}
                  </>
                ) : (
                  <span>Select date range</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start" side="bottom">
              <div className="flex">
                {/* Presets sidebar */}
                <div className="border-r p-2 space-y-0.5 w-[140px]">
                  <Button
                    variant="ghost"
                    className={cn(
                      "w-full justify-start text-xs font-normal h-8 px-2",
                      selectedPreset === "last_30_days" && "bg-[#FF6600] text-white hover:bg-[#FF6600]/90 hover:text-white"
                    )}
                    onClick={() => selectDatePreset("last_30_days")}
                  >
                    Last 30 days
                  </Button>
                  <Button
                    variant="ghost"
                    className={cn(
                      "w-full justify-start text-xs font-normal h-8 px-2",
                      selectedPreset === "last_60_days" && "bg-[#FF6600] text-white hover:bg-[#FF6600]/90 hover:text-white"
                    )}
                    onClick={() => selectDatePreset("last_60_days")}
                  >
                    Last 60 days
                  </Button>
                  <Button
                    variant="ghost"
                    className={cn(
                      "w-full justify-start text-xs font-normal h-8 px-2",
                      selectedPreset === "last_90_days" && "bg-[#FF6600] text-white hover:bg-[#FF6600]/90 hover:text-white"
                    )}
                    onClick={() => selectDatePreset("last_90_days")}
                  >
                    Last 90 days
                  </Button>
                  <Button
                    variant="ghost"
                    className={cn(
                      "w-full justify-start text-xs font-normal h-8 px-2",
                      selectedPreset === "last_180_days" && "bg-[#FF6600] text-white hover:bg-[#FF6600]/90 hover:text-white"
                    )}
                    onClick={() => selectDatePreset("last_180_days")}
                  >
                    Last 180 days
                  </Button>
                  <div className="border-t my-1"></div>
                  <Button
                    variant="ghost"
                    className={cn(
                      "w-full justify-start text-xs font-normal h-8 px-2",
                      selectedPreset === "this_year" && "bg-[#FF6600] text-white hover:bg-[#FF6600]/90 hover:text-white"
                    )}
                    onClick={() => selectDatePreset("this_year")}
                  >
                    This year
                  </Button>
                  <Button
                    variant="ghost"
                    className={cn(
                      "w-full justify-start text-xs font-normal h-8 px-2",
                      selectedPreset === "last_year" && "bg-[#FF6600] text-white hover:bg-[#FF6600]/90 hover:text-white"
                    )}
                    onClick={() => selectDatePreset("last_year")}
                  >
                    Last year
                  </Button>
                  <div className="border-t my-1"></div>
                  <Button
                    variant="ghost"
                    className={cn(
                      "w-full justify-start text-xs font-normal h-8 px-2",
                      selectedPreset === "custom" && "bg-[#FF6600] text-white hover:bg-[#FF6600]/90 hover:text-white"
                    )}
                    onClick={() => selectDatePreset("custom")}
                  >
                    Custom
                  </Button>
                </div>

                {/* Calendar section */}
                <div className="p-3 flex flex-col">
                  <Calendar
                    mode="range"
                    selected={{
                      from: tempDateFrom,
                      to: tempDateTo
                    }}
                    onSelect={(range: DateRange | undefined) => {
                      if (range?.from) {
                        setTempDateFrom(range.from);
                        setTempDateTo(range.to);
                        setSelectedPreset("custom");
                      }
                    }}
                    numberOfMonths={2}
                    initialFocus
                    className="border-0"
                  />

                  {/* Apply button */}
                  <div className="border-t pt-3 mt-3 flex justify-end">
                    <Button
                      onClick={handleApplyDates}
                      className="bg-[#FF6600] hover:bg-[#FF6600]/90 text-white"
                      disabled={!tempDateFrom || !tempDateTo}
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Widgets Grid */}
          {widgets.length === 0 ? (
            <Card>
              <CardContent className="flex items-center justify-center h-48 text-center">
                <p className="text-muted-foreground">
                  No widgets configured for this dashboard
                </p>
              </CardContent>
            </Card>
          ) : (
            <ResponsiveGridLayout
              className="layout"
              layout={widgets.map(w => ({
                i: w.i,
                x: w.x,
                y: w.y,
                w: w.w,
                h: w.h,
                minW: 2,
                minH: 2,
                maxW: 24,
                maxH: 20,
              }))}
              cols={24}
              rowHeight={60}
              isDraggable={false}
              isResizable={false}
              compactType={null}
            >
              {widgets.map((widget) => (
                <div key={widget.i} className="bg-background rounded-lg border shadow-sm">
                  <Card className="h-full flex flex-col">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">{widget.title || "Untitled Widget"}</CardTitle>
                    </CardHeader>
                    <CardContent className="flex-1 pt-0 pb-4 overflow-auto">
                      <div className="h-full">
                        <WidgetDisplay widget={widget} dateRange={dateRange} />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </ResponsiveGridLayout>
          )}
        </div>
      </CubeProvider>
    </BackofficeLayout>
  );
};

export default AnalyticsDashboard;
